import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Badge,
    Button,
    Code,
    Group,
    Modal,
    Stack,
    Text,
} from '@mantine/core';

import {
    DnsRecord,
    isDeletableRecord,
} from '../types';
import {recordValueText} from '../recordValue';
import {classifyWriteError} from '../../zones/services/writeErrors';
import {recordWriteErrorCopy} from '../writeErrorMessages';

// FNP-1192 — THE DELETE CONFIRMATION.
//
// ⚠️ A CONTROLLED Mantine `Modal`, NOT `modals.openConfirmModal`.
//
// @mantine/modals is a dependency but NO `ModalsProvider` is mounted in
// src/App.tsx, so `modals.openConfirmModal` silently does nothing — the worst
// possible failure for a destructive-action guard, because the button would
// appear inert rather than broken. The zones module reached the same conclusion
// for the same reason.
//
// ⚠️ THIS MODAL NAMES THE RECORD AND SHOWS WHAT IT ANSWERS WITH. "Delete 1
// item?" is a rubber stamp — the operator already knows they clicked delete.
// What they may not know is that this record is the one their mail or their
// site depends on, and the value is what tells them.
export const DeleteRecordModal = (
    {
        record,
        zoneName,
        opened,
        onClose,
        onConfirm,
        isDeleting = false,
        error,
    }: {
        record?: DnsRecord,
        zoneName?: string,
        opened: boolean,
        onClose: () => void,
        onConfirm: () => void,
        isDeleting?: boolean,
        // The rejected mutation, unclassified. Classified here so the refusal
        // renders INLINE — a toast is gone before an operator has finished
        // reading it.
        error?: unknown,
    },
) => {

    if (!record) {
        return null;
    }

    const writeError = error ? classifyWriteError(error) : undefined;

    // ⚠️ DELETING AN NS RECORD BREAKS A DELEGATION, and that is worth saying
    // out loud rather than leaving the operator to realise it afterwards. The
    // nameserver records for a subdomain ARE the delegation — removing them
    // does not tidy anything, it takes the whole subdomain offline.
    const isDelegation = record.type === 'NS';

    // FNP-1279 — a pointer cannot be deleted through this path. The confirm
    // button is disabled rather than the modal refused to open: the operator
    // asked about this record and is owed the explanation, not a dead click.
    const isPointer = !isDeletableRecord(record);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={I18n.get('Delete record')}
            size={'lg'}
            centered={true}
        >
            <Stack gap={'md'}>

                <Stack gap={4}>
                    <Text size={'sm'}>{I18n.get('You are about to delete this record:')}</Text>
                    <Group gap={'xs'}>
                        <Code>{record.name}</Code>
                        <Badge variant={'light'} color={isDelegation ? 'grape' : 'blue'}>
                            {record.type}
                        </Badge>
                        {record.isPointer && (
                            <Badge variant={'outline'} color={'cyan'} size={'sm'}>
                                {I18n.get('Alias')}
                            </Badge>
                        )}
                    </Group>
                    <Text size={'xs'} c={'dimmed'} style={{whiteSpace: 'pre-line'}}>
                        {recordValueText(record)}
                    </Text>
                    {zoneName && (
                        <Text size={'xs'} c={'dimmed'}>
                            {I18n.get('Zone')}: <Code>{zoneName}</Code>
                        </Text>
                    )}
                </Stack>

                {/* ⚠️ THE BADGE ABOVE ALREADY SAID THIS; NOW THE CONTROL
                    FOLLOWS IT (FNP-1279). The modal used to render the Alias
                    badge and then offer the delete button anyway — the app
                    held the fact and did not act on it. A pointer delete
                    cannot be built from what Flexi holds: the provider matches
                    on the full alias target, so the change either fails or
                    removes a different record of the same name and type. */}
                {isPointer && (
                    <Alert
                        color={'blue'}
                        variant={'light'}
                        title={I18n.get('This record cannot be deleted here')}
                    >
                        {productCopy('It points at another resource rather than holding a value of its own. Removing it needs the full alias target, which {product} does not hold, so it must be deleted at the provider.')}
                    </Alert>
                )}

                {isDelegation && (
                    <Alert color={'yellow'} variant={'light'} title={I18n.get('This record is a delegation')}>
                        {I18n.get('Nameserver records are what delegate a subdomain to another provider. Deleting them does not tidy the zone — it takes that subdomain offline until the delegation is restored.')}
                    </Alert>
                )}

                {writeError && (
                    <Alert
                        color={'red'}
                        variant={'light'}
                        title={recordWriteErrorCopy(writeError.kind).title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>
                                {recordWriteErrorCopy(writeError.kind).action}
                            </Text>
                            {/* The provider's own words, VERBATIM. */}
                            {writeError.providerMessage && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {writeError.providerMessage}
                                </Text>
                            )}
                            <Code>{writeError.kind}</Code>
                        </Stack>
                    </Alert>
                )}

                {/* ⚠️ DOES NOT PROMISE THE DELETION IS INSTANT. The provider
                    accepts the change before it propagates, and resolvers
                    elsewhere keep answering with this value until the TTL
                    expires. Saying "removed" alone would be the same
                    over-claim the write states exist to prevent. */}
                <Text size={'sm'}>
                    {I18n.get('This cannot be undone. Resolvers may keep answering with this value until its time to live expires.')}
                </Text>

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose} disabled={isDeleting}>
                        {I18n.get('Cancel')}
                    </Button>
                    <Button
                        color={'red'}
                        onClick={onConfirm}
                        loading={isDeleting}
                        disabled={isDeleting || isPointer}
                    >
                        {I18n.get('Delete record')}
                    </Button>
                </Group>

            </Stack>
        </Modal>
    );
};
